import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "aek-cart-v1";

const signatureOf = (item) => {
  if (item.type === "kit") {
    const sel = {};
    Object.keys(item.selections || {})
      .sort()
      .forEach((k) => (sel[k] = item.selections[k]));
    const acc = (item.accessories || []).slice().sort();
    return `kit::${item.name}::${JSON.stringify({ sel, acc })}`;
  }
  const opts = {};
  Object.keys(item.options || {})
    .sort()
    .forEach((k) => (opts[k] = item.options[k]));
  return `${item.productId}::${JSON.stringify(opts)}`;
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage unavailable */
    }
  }, [items]);

  const addItem = useCallback((item) => {
    let merged = false;
    setItems((prev) => {
      const sig = signatureOf(item);
      const existing = prev.find((i) => i._sig === sig);
      if (existing) {
        merged = true;
        return prev.map((i) =>
          i._sig === sig
            ? { ...i, qty: Math.min(i.qty + (item.qty || 1), 99) }
            : i,
        );
      }
      return [
        ...prev,
        {
          ...item,
          qty: item.qty || 1,
          _sig: sig,
          key: `${sig}-${Date.now()}`,
        },
      ];
    });
    return merged;
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === key ? { ...i, qty: Math.max(1, Math.min(qty, 99)) } : i,
      ),
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((n, i) => n + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((n, i) => n + i.unitPrice * i.qty, 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      count,
      subtotal,
    }),
    [items, addItem, updateQty, removeItem, clearCart, count, subtotal],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
